import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Check, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { orderService } from '../services/api';

const OrderSearchSelect = ({ currentOrderId, currentOrderData, onUnlink, onLink }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showResults, setShowResults] = useState(false);

    // Si ya tiene un pedido asignado, mostrarlo (aunque solo tenemos el ID, idealmente el backend mandaría más info)
    // Por simplicidad, si hay ID lo mostramos "Pedido #ID".

    const wrapperRef = useRef(null);

    useEffect(() => {
        // Cerrar resultados si clic fuera
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowResults(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm.length >= 2) {
                searchOrders();
            } else {
                setResults([]);
            }
        }, 300); // Debounce de 300ms
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const searchOrders = async () => {
        setLoading(true);
        try {
            const data = await orderService.searchOrdersLite(searchTerm);
            setResults(data);
            setShowResults(true);
        } catch (error) {
            console.error("Error buscando pedidos", error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = async (order) => {
        setSelectedOrder(order);
        setSearchTerm(`Pedido #${order.order_number} - ${order.customer_name}`);
        setShowResults(false);

        try {
            await onLink(order); // Pasamos objeto completo
            setIsEditing(false);
            toast.success('Pedido vinculado exitosamente');
        } catch (e) {
            toast.error('Error vinculando pedido');
            setSearchTerm('');
        }
    };

    const handleUnlink = async () => {
        if (window.confirm('¿Seguro quieres desvincular este pedido?')) {
            try {
                await onUnlink();
                toast.success('Pedido desvinculado');
            } catch (e) {
                toast.error('Error desvinculando');
            }
        }
    };

    if (!isEditing && currentOrderId) {
        return (
            <div className="flex items-center space-x-2">
                <div
                    className="text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded flex flex-col items-start min-w-[160px]"
                    title={currentOrderData ? `Pedido #${currentOrderData.order_number} - ${currentOrderData.customer_name}` : `Pedido #${currentOrderId}`}
                >
                    {currentOrderData && currentOrderData.order_number ? (
                        <>
                            <span className="font-bold text-xs text-blue-800 tracking-wide mb-0.5">
                                #{currentOrderData.order_number}
                            </span>
                            <span className="font-medium leading-4 text-left text-blue-700 break-words w-full">
                                {currentOrderData.customer_name || 'Sin nombre'}
                            </span>
                        </>
                    ) : (
                        <span className="font-medium text-xs text-blue-800">
                            Pedido #{currentOrderId} <br />
                            <span className="text-gray-500 font-normal opacity-75">(Datos no disp.)</span>
                        </span>
                    )}
                </div>
                <button onClick={handleUnlink} className="text-gray-400 hover:text-red-500 p-1 hover:bg-red-50 rounded" title="Desvincular">
                    <X size={16} />
                </button>
            </div>
        );
    }

    if (!isEditing && !currentOrderId) {
        return (
            <button
                onClick={() => setIsEditing(true)}
                className="text-xs text-gray-400 hover:text-blue-500 flex items-center gap-1 border border-transparent hover:border-gray-200 px-2 py-1 rounded"
            >
                + Vincular
            </button>
        );
    }

    return (
        <div className="relative w-64" ref={wrapperRef}>
            <div className="relative">
                <input
                    type="text"
                    className="w-full text-sm border rounded pl-2 pr-8 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="Buscar pedido o cliente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
                <button
                    onClick={() => setIsEditing(false)}
                    className="absolute right-1 top-1 text-gray-400 hover:text-gray-600 p-0.5"
                >
                    <X size={14} />
                </button>
            </div>

            {showResults && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto text-sm">
                    {loading && <div className="p-2 text-center text-gray-500">Buscando...</div>}
                    {!loading && results.length === 0 && searchTerm.length >= 2 && (
                        <div className="p-2 text-center text-gray-500">No se encontraron pedidos</div>
                    )}
                    {results.map(order => (
                        <div
                            key={order.id}
                            className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-0"
                            onClick={() => handleSelect(order)}
                        >
                            <div className="font-semibold text-gray-800">Pedido #{order.order_number}</div>
                            <div className="text-xs text-gray-600">{order.customer_name}</div>
                            <div className="text-xs text-gray-500 text-right font-mono">
                                ${new Intl.NumberFormat('es-CO').format(order.total)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default OrderSearchSelect;
